// AdminNotificationPage.jsx - Integrated Push Notification Management System
import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
// // // import { useTranslation } from "react-i18next";   // Admin은 다국어 지원 불필요// Admin은 다국어 지원 불필요// Admin은 다국어 지원 불필요
import { useToastMessage } from "../contexts/ToastMessageContext.jsx";
import { useUser } from "../contexts/UserContext.jsx";
import { db, appId } from "../firebase/firebaseConfig.jsx";
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  serverTimestamp, 
  doc, 
  updateDoc, 
  deleteDoc,
  getDocs,
  where,
  limit,
  startAfter,
  writeBatch
} from "firebase/firestore";
import { 
  Card, 
  Button, 
  Input, 
  Select, 
  Modal, 
  Form, 
  Radio, 
  Switch, 
  DatePicker, 
  TimePicker, 
  Table, 
  Tag, 
  Space, 
  Row, 
  Col, 
  Statistic, 
  Progress, 
  Alert, 
  Tabs, 
  Typography, 
  Divider,
  Tooltip,
  Badge,
  Empty,
  Spin,
  Checkbox
} from "antd";
import { calculatePolicyCompliance } from '../../../shared/utils/policyComplianceCalculator';
import { 
  SendOutlined, 
  SearchOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  SettingOutlined,
  MonitorOutlined,
  SyncOutlined,
  AlertOutlined,
  CheckSquareOutlined,
  CloseSquareOutlined,
} from "@ant-design/icons";
import { 
  Bell, 
  Send, 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  AlertCircle, 
  AlertTriangle, 
  Info, 
  CheckCircle, 
  User, 
  Users, 
  Clock, 
  BarChart3, 
  Settings, 
  Monitor,
  Activity,
  MessageSquare,
  Target,
  Calendar,
  X,
  Check,
  XCircle,
  ChevronLeft,
  ChevronRight,
  RefreshCw
} from "lucide-react";
import { adminNotificationService } from "../services/adminNotificationService.js";
import dayjs from "dayjs";

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const { Title, Text, Paragraph } = Typography;

const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const AdminNotificationPage = () => {
  // // const { t } = useTranslation();  // Admin은 다국어 지원 불필요// Admin은 다국어 지원 불필요
  const { showToastMessage } = useToastMessage();
  const { user, profile } = useUser();
  
  // State management
  const [notifications, setNotifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [systemNotifications, setSystemNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // 성능 최적화: 사용자 데이터 캐싱
  const [usersCache, setUsersCache] = useState(null);
  const [lastUsersLoadTime, setLastUsersLoadTime] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSender, setFilterSender] = useState("all");
  
  // 실시간 모니터링 상태
  const [monitoringStats, setMonitoringStats] = useState({
    totalNotifications: 0,
    successRate: 0,
    failureRate: 0,
    activeTokens: 0,
    lastUpdate: null,
    systemHealth: 'healthy'
  });
  const [isMonitoringActive, setIsMonitoringActive] = useState(true);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalNotifications, setTotalNotifications] = useState(0);
  
  // Modal states (isCreateModalVisible 제거됨 - 폼이 항상 메인 영역에 표시됨)
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isMonitoringModalVisible, setIsMonitoringModalVisible] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [notificationDetailModal, setNotificationDetailModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  
  // Form states
  const [form] = Form.useForm();
  const [notificationForm, setNotificationForm] = useState({
    title: "",
    message: "",
    type: "normal", // normal, urgent, warning
    priority: "medium",
    sender: "admin", // admin, myanmar_labor
    targetType: "all", // all, inactive_users, specific_users
    targetUsers: [],
    scheduledAt: null,
    isScheduled: false,
    sendPushNotification: true,
    requireInteraction: false
  });
  
  
  // Send monitoring data state
  const [sendMonitoringData, setSendMonitoringData] = useState({
    performanceMetrics: {
      averageDeliveryTime: 0,
      successRate: 0,
      errorRate: 0
    }
  });


  // User selection states
  const [isUserSelectionModalVisible, setIsUserSelectionModalVisible] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [isLoadingMoreUsers, setIsLoadingMoreUsers] = useState(false);
  const [searchFilters, setSearchFilters] = useState({
    hasRemittance: "", // "": all, true: has remittance, false: no remittance
    remittanceDays: "", // "": all, number: days since last remittance
    userStatus: 'all' // all, active, inactive
  });

  // Constants
  const NOTIFICATION_TYPES = {
    normal: { label: "Normal", color: "blue", icon: <InfoCircleOutlined /> },
    urgent: { label: "Urgent", color: "red", icon: <ExclamationCircleOutlined /> },
    warning: { label: "Warning", color: "orange", icon: <WarningOutlined /> }
  };

  const SENDER_OPTIONS = [
    { value: "admin", label: "Admin", icon: "👨‍💼" },
    { value: "myanmar_labor", label: "Ministry of Labour", icon: "🏛️" }
  ];

  // 실시간 모니터링 통계 계산
  const calculateMonitoringStats = useCallback((notificationsData) => {
    const total = notificationsData.length;
    const sent = notificationsData.filter(n => n.status === 'sent').length;
    const failed = notificationsData.filter(n => n.status === 'failed').length;
    const pending = notificationsData.filter(n => n.status === 'pending' || !n.status).length;
    
    const successRate = total > 0 ? Math.round((sent / total) * 100) : 0;
    const failureRate = total > 0 ? Math.round((failed / total) * 100) : 0;
    
    // 시스템 건강도 계산
    let systemHealth = 'healthy';
    if (failureRate > 20) {
      systemHealth = 'critical';
    } else if (failureRate > 10) {
      systemHealth = 'warning';
    } else if (pending > 5) {
      systemHealth = 'attention';
    }
    
    return {
      totalNotifications: total,
      successRate,
      failureRate,
      pendingCount: pending,
      activeTokens: users.length, // 전체 사용자 수를 활성 토큰으로 간주
      lastUpdate: new Date(),
      systemHealth
    };
  }, [users.length]);

  // Load notifications
  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
    const notificationsRef = collection(db, `artifacts/${appId}/notifications`);
      const q = query(notificationsRef, orderBy("createdAt", "desc"));
      const snapshot = await getDocs(q);
    
      const notificationsData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
        id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
          // Ensure targetType is properly set - Set to specific_users for KYC notifications
          targetType: data.targetType || (data.targetUsers && data.targetUsers.length > 0 ? 'specific_users' : 'all'),
          // Ensure targetUsers is properly set
          targetUsers: data.targetUsers || [],
          // Ensure status is properly set (maintain existing notifications without status)
          status: data.status,
          // Ensure type is properly set
          type: data.type || 'normal'
        };
      });
      
      setNotifications(notificationsData);
      setTotalNotifications(notificationsData.length);
      
      // 실시간 모니터링 통계 업데이트
      if (isMonitoringActive) {
        const stats = calculateMonitoringStats(notificationsData);
        setMonitoringStats(stats);
      }
      
      console.log('Total notifications loaded:', notificationsData.length);
      console.log('First notification:', notificationsData[0]);
      console.log('All notifications data:', notificationsData.map(n => ({
        id: n.id,
        title: n.title,
        status: n.status,
        sentAt: n.sentAt,
        sentCount: n.sentCount,
        targetCount: n.targetCount,
        read: n.read,
        clicked: n.clicked,
        createdAt: n.createdAt,
        // User notification data
        fullData: n
      })));
      
      // read/clicked data
      console.log('Read/clicked data:', notificationsData.map(n => ({
        id: n.id,
        title: n.title,
        hasRead: 'read' in n,
        readValue: n.read,
        hasClicked: 'clicked' in n,
        clickedValue: n.clicked,
        allFields: Object.keys(n)
      })));
      
      // Today's notifications
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayNotifications = notificationsData.filter(n => {
        const createdDate = n.createdAt?.toDate ? n.createdAt.toDate() : new Date(n.createdAt);
        return createdDate >= today;
      });
      console.log('Today notifications count:', todayNotifications.length);
      console.log('Today notifications data:', todayNotifications.map(n => ({
        id: n.id,
        title: n.title,
        status: n.status,
        sentAt: n.sentAt,
        createdAt: n.createdAt
      })));
    } catch (error) {
      console.error("Error loading notifications:", error);
      showToastMessage("Failed to load notifications. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  }, [showToastMessage, isMonitoringActive, calculateMonitoringStats]);

  // Admin 권한 확인 함수
  const checkAdminPermissions = useCallback(() => {
    console.log('🔍 Admin 권한 확인 - 상세 분석:');
    console.log('  📊 user 객체:', user);
    console.log('  📊 user.uid:', user?.uid);
    console.log('  📊 user.email:', user?.email);
    console.log('  📊 user.role:', user?.role);
    console.log('  📊 user.admin:', user?.admin);
    console.log('  📊 user.customClaims:', user?.customClaims);
    console.log('  📊 profile 객체:', profile);
    console.log('  📊 profile.role:', profile?.role);
    console.log('  📊 profile.admin:', profile?.admin);
    
    // 여러 방법으로 Admin 권한 확인
    const isAdminByUser = user?.role === 'admin' || user?.admin === true;
    const isAdminByProfile = profile?.role === 'admin' || profile?.admin === true;
    const isAdminByCustomClaims = user?.customClaims?.admin === true || user?.customClaims?.role === 'admin';
    
    const isAdmin = isAdminByUser || isAdminByProfile || isAdminByCustomClaims;
    
    console.log('🔍 Admin 권한 확인 결과:');
    console.log('  ✅ user.role === "admin":', user?.role === 'admin');
    console.log('  ✅ user.admin === true:', user?.admin === true);
    console.log('  ✅ profile.role === "admin":', profile?.role === 'admin');
    console.log('  ✅ profile.admin === true:', profile?.admin === true);
    console.log('  ✅ customClaims.admin === true:', user?.customClaims?.admin === true);
    console.log('  ✅ customClaims.role === "admin":', user?.customClaims?.role === 'admin');
    console.log('  🎯 최종 isAdmin 결과:', isAdmin);
    
    if (!isAdmin) {
      console.warn('⚠️ Admin 권한이 없습니다. 사용자 조회가 제한될 수 있습니다.');
      console.warn('⚠️ 권한 확인 방법별 결과:');
      console.warn('  - user 기반:', isAdminByUser);
      console.warn('  - profile 기반:', isAdminByProfile);
      console.warn('  - customClaims 기반:', isAdminByCustomClaims);
    }
    
    return isAdmin;
  }, [user, profile, appId]);


  // Load users with pagination and admin filtering
  const loadUsers = useCallback(async (forceReload = false, pageLimit = 50) => {
    try {
      // 성능 최적화: 캐시 확인 (5분간 캐시 유지)
      const now = Date.now();
      const cacheExpiry = 5 * 60 * 1000; // 5분
      
      if (!forceReload && usersCache && lastUsersLoadTime && (now - lastUsersLoadTime) < cacheExpiry) {
        console.log('📦 캐시된 사용자 데이터 사용');
        setUsers(usersCache);
        return;
      }
      
      console.log('🔄 사용자 목록 로딩 시작... (페이지네이션 적용)');
      console.log('🔍 현재 Admin 사용자 정보:', {
        uid: user?.uid,
        email: user?.email,
        role: user?.role,
        admin: user?.admin,
        customClaims: user?.customClaims
      });
      
      // Admin 권한 확인
      const hasAdminPermission = checkAdminPermissions();
      console.log('🔍 Admin 권한 확인 결과:', hasAdminPermission);
      
      const hardcodedAppId = 'kairos-platform-1ffde';
      const usersData = [];
      const processedUserIds = new Set();
      
      // 메인 경로에서만 일반 사용자 조회 (어드민 제외)
      const userCollectionPaths = [
        `artifacts/${hardcodedAppId}/users`  // 메인 경로만 사용
      ];
      
      // 각 경로에서 사용자 정보 수집 (페이지네이션 적용)
      for (const collectionPath of userCollectionPaths) {
        try {
          console.log(`📍 경로 검색: ${collectionPath} (제한: 100명)`);
          const usersRef = collection(db, collectionPath);
          
          // 페이지네이션을 위한 쿼리 (최신 사용자부터)
          const usersQuery = query(
            usersRef,
            orderBy('createdAt', 'desc'),
            limit(100)
          );
          
          const usersSnapshot = await getDocs(usersQuery);
          console.log(`📊 ${collectionPath} 문서 수:`, usersSnapshot.docs.length);
          
          for (const userDoc of usersSnapshot.docs) {
            const userData = userDoc.data();
            const userId = userDoc.id;
            
            // 어드민 사용자 제외
            const isAdmin = userData.role === 'admin' || 
                           userData.admin === true || 
                           userData.isAdmin === true ||
                           (userData.customClaims && userData.customClaims.admin === true);
            
            if (isAdmin) {
              console.log(`🚫 어드민 사용자 제외: ${userId} (${userData.email})`);
              continue;
            }
            
            // 이미 처리된 사용자인지 확인
            if (processedUserIds.has(userId)) {
              console.log(`⏭️ 이미 처리된 사용자: ${userId}`);
              continue;
            }
            
            console.log(`👤 일반 사용자 발견: ${userId}`, {
              email: userData.email,
              role: userData.role,
              displayName: userData.displayName,
              name: userData.name,
              fullName: userData.fullName
            });
            
            // 성능 최적화: 필요한 필드만 추출
            const userInfo = {
              id: userId,
              email: userData.email,
              name: userData.displayName || userData.name || userData.fullName || userData.email?.split('@')[0] || '이름 없음',
              displayName: userData.displayName || userData.name || userData.fullName || userData.email?.split('@')[0] || '이름 없음',
              phone: userData.phone || userData.phoneNumber || '',
              role: userData.role || 'user',
              active: userData.active !== false,
              lastActivity: userData.lastActivity?.toDate?.() || new Date(),
              createdAt: userData.createdAt?.toDate?.() || new Date(),
            };
            
            usersData.push(userInfo);
            processedUserIds.add(userId);
            console.log(`✅ 일반 사용자 추가 완료: ${userInfo.displayName || userInfo.email || userId}`);
          }
        } catch (error) {
          console.warn(`❌ ${collectionPath} 로드 실패:`, error.message);
          // 개별 경로 실패는 전체를 중단시키지 않음
        }
      }
      
      console.log('✅ 최종 사용자 수집 결과:');
      console.log(`📊 총 발견된 사용자: ${usersData.length}명`);
      
      // Load remittance data to check last remittance date
      const remittancesRef = collection(db, `artifacts/${hardcodedAppId}/remittances`);
      const remittancesSnapshot = await getDocs(remittancesRef);
      
      // Create a map of user ID to last remittance date
      const userLastRemittance = new Map();
      
      remittancesSnapshot.docs.forEach(doc => {
        const remittanceData = doc.data();
        const userId = remittanceData.userId || remittanceData.user_id;
        const remittanceDate = remittanceData.createdAt?.toDate?.() || 
                              remittanceData.timestamp?.toDate?.() || 
                              remittanceData.date?.toDate?.() ||
                              new Date(remittanceData.createdAt || remittanceData.timestamp || remittanceData.date);
        
        if (userId) {
          const existingDate = userLastRemittance.get(userId);
          if (!existingDate || remittanceDate > existingDate) {
            userLastRemittance.set(userId, remittanceDate);
          }
        }
      });
      
      console.log('Remittance data processed for', userLastRemittance.size, 'users');
      
      // Add last remittance date to all users
      const usersWithRemittanceData = usersData.map(user => ({
        ...user,
        lastRemittance: userLastRemittance.get(user.id)
      }));
      
      setUsers(usersWithRemittanceData);
      console.log('✅ 총 사용자 로딩 완료:', usersWithRemittanceData.length);
      
      // Filter users with no remittance activity for 30+ days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const inactiveUsersData = usersData.filter(user => {
        const lastRemittanceDate = userLastRemittance.get(user.id);
        
        // If user has no remittance record, consider them inactive
        if (!lastRemittanceDate) {
          console.log(`User ${user.id} has no remittance record`);
          return true;
        }
        
        // If last remittance was more than 30 days ago, consider them inactive
        const isInactive = lastRemittanceDate < thirtyDaysAgo;
        if (isInactive) {
          const daysSinceRemittance = Math.floor((new Date() - lastRemittanceDate) / (1000 * 60 * 60 * 24));
          console.log(`User ${user.id} last remittance: ${lastRemittanceDate.toLocaleDateString()}, ${daysSinceRemittance} days ago`);
        }
        
        return isInactive;
      }).map(user => ({
        ...user,
        lastRemittance: userLastRemittance.get(user.id)
      }));
      
      console.log('Inactive users (30+ days without remittance):', inactiveUsersData.length);
      console.log('Inactive users details:', inactiveUsersData.map(user => ({
        id: user.id,
        displayName: user.displayName || user.email,
        lastRemittance: userLastRemittance.get(user.id)?.toLocaleDateString() || 'No remittance record'
      })));
      
      setInactiveUsers(inactiveUsersData);
      
      // 사용자 목록 업데이트 (기존 목록이 있으면 추가, 없으면 새로 설정)
      setUsers(prevUsers => {
        if (prevUsers.length === 0) {
          // 초기 로딩
          console.log(`📊 초기 사용자 목록 설정: ${usersData.length}명`);
          setUsersCache(usersData);
          setLastUsersLoadTime(Date.now());
          return usersData;
        } else {
          // 추가 로딩 (중복 제거)
          const existingIds = new Set(prevUsers.map(u => u.id));
          const newUsers = usersData.filter(u => !existingIds.has(u.id));
          const combinedUsers = [...prevUsers, ...newUsers];
          
          console.log(`📊 사용자 목록 업데이트: 기존 ${prevUsers.length}명 + 신규 ${newUsers.length}명 = 총 ${combinedUsers.length}명`);
          
          // 캐시 저장
          setUsersCache(combinedUsers);
          setLastUsersLoadTime(Date.now());
          console.log('💾 사용자 데이터 캐시 저장 완료');
          
          return combinedUsers;
        }
      });
      
    } catch (error) {
      console.error("❌ 사용자 및 송금 데이터 로딩 실패:", error);
      console.error("❌ 오류 상세:", {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
    }
  }, [checkAdminPermissions, usersCache, lastUsersLoadTime]);

  // Load system notifications
  const loadSystemNotifications = useCallback(async () => {
    try {
      const systemRef = collection(db, `artifacts/${appId}/system_notifications`);
      const q = query(systemRef, orderBy("createdAt", "desc"), limit(50));
      const snapshot = await getDocs(q);
      
      const systemData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
      }));

      setSystemNotifications(systemData);
    } catch (error) {
      console.error("Error loading system notifications:", error);
    }
  }, []);

  // Validate notification data consistency
  const validateNotificationDataConsistency = (notification) => {
    const issues = [];
    
    // Check required fields
    if (!notification.title || !notification.message || !notification.type) {
      issues.push('missing_required_fields');
    }
    
    // Check data consistency
    if (notification.sentCount && notification.targetCount && notification.sentCount > notification.targetCount) {
      issues.push('sent_count_exceeds_target');
    }
    
    if (notification.read && notification.sentCount && notification.read > notification.sentCount) {
      issues.push('read_count_exceeds_sent');
    }
    
    if (notification.clicked && notification.sentCount && notification.clicked > notification.sentCount) {
      issues.push('clicked_count_exceeds_sent');
    }
    
    return {
      isValid: issues.length === 0,
      issues: issues
    };
  };

  // Calculate statistics with data integrity validation
  const calculateStats = useCallback(async () => {
    // Function removed - statistics section deleted
    return;
  }, [notifications, inactiveUsers]);

  // Calculate advanced analytics metrics
  const calculateAdvancedAnalytics = useCallback(async () => {
    // Function removed - statistics section deleted
    return;
  }, []);

  // Update send monitoring data
  const updateSendMonitoringData = useCallback(async () => {
    try {
      console.log('Updating send monitoring data...');
      
      // Calculate performance metrics
      const performanceMetrics = await calculatePerformanceMetrics();
      
      setSendMonitoringData(prev => ({
        ...prev,
        performanceMetrics
      }));
      
      console.log('Send monitoring data updated');
    } catch (error) {
      console.error('Failed to update send monitoring data:', error);
    }
  }, [notifications]);


  // Calculate performance metrics
  const calculatePerformanceMetrics = useCallback(async () => {
    try {
      const oneDayAgo = new Date();
      oneDayAgo.setDate(oneDayAgo.getDate() - 1);
      
      const recentNotifications = notifications.filter(n => {
        const createdAt = n.createdAt?.toDate?.() || new Date(n.createdAt);
        return createdAt >= oneDayAgo;
      });
      
      // Calculate average delivery time
      let totalDeliveryTime = 0;
      let deliveryCount = 0;
      
      recentNotifications.forEach(n => {
        if (n.sentAt && n.createdAt) {
          const sentTime = n.sentAt.toDate ? n.sentAt.toDate() : new Date(n.sentAt);
          const createdTime = n.createdAt.toDate ? n.createdAt.toDate() : new Date(n.createdAt);
          const deliveryTime = sentTime.getTime() - createdTime.getTime();
          
          if (deliveryTime > 0 && deliveryTime < 300000) { // 5 minutes maximum delivery time
            totalDeliveryTime += deliveryTime;
            deliveryCount++;
          }
        }
      });
      
      const averageDeliveryTime = deliveryCount > 0 ? totalDeliveryTime / deliveryCount : 0;
      
      // Calculate success rate
      const totalSent = recentNotifications.reduce((sum, n) => sum + (n.targetCount || 0), 0);
      const totalDelivered = recentNotifications.reduce((sum, n) => sum + (n.sentCount || 0), 0);
      const successRate = totalSent > 0 ? (totalDelivered / totalSent) * 100 : 0;
      
      // Calculate error rate
      const totalFailed = recentNotifications.reduce((sum, n) => sum + (n.failedCount || 0), 0);
      const errorRate = totalSent > 0 ? (totalFailed / totalSent) * 100 : 0;
      
      return {
        averageDeliveryTime: Math.round(averageDeliveryTime),
        successRate: parseFloat(successRate.toFixed(2)),
        errorRate: parseFloat(errorRate.toFixed(2))
      };
    } catch (error) {
      console.error('Failed to calculate performance metrics:', error);
      return {
        averageDeliveryTime: 0,
        successRate: 0,
        errorRate: 0
      };
    }
  }, [notifications]);





  // Create test notifications if none exist
  const createTestNotifications = useCallback(async () => {
    try {
      const notificationsRef = collection(db, `artifacts/${appId}/notifications`);
      const snapshot = await getDocs(notificationsRef);
      
      if (snapshot.empty) {
        console.log('Creating test notifications...');
        const testNotifications = [
          {
            title: "Welcome to Kairos Platform",
            message: "Thank you for joining our remittance platform. Start your first transaction today!",
            type: "normal",
            targetType: "all",
            targetUsers: [],
            read: false,
            status: "sent",
            sender: "admin",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: "admin",
            targetCount: 0
          },
          {
            title: "System Maintenance Notice",
            message: "Scheduled maintenance will occur tonight from 2 AM to 4 AM. Some services may be temporarily unavailable.",
            type: "warning",
            targetType: "all",
            targetUsers: [],
            read: false,
            status: "sent",
            sender: "admin",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: "admin",
            targetCount: 0
          },
          {
            title: "Urgent: Security Update Required",
            message: "Please update your app to the latest version for security improvements.",
            type: "urgent",
            targetType: "inactive_users",
            targetUsers: [],
            read: false,
            status: "pending",
            sender: "admin",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: "admin",
            targetCount: 0
          },
          {
            title: "Specific User Notification",
            message: "This is a test notification for specific users.",
            type: "normal",
            targetType: "specific_users",
            targetUsers: ["user1", "user2", "user3"],
            read: false,
            status: "sent",
            sender: "admin",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: "admin",
            targetCount: 3
          }
        ];
        
        for (const notification of testNotifications) {
          await addDoc(notificationsRef, notification);
        }
        
        console.log('Test notifications created successfully');
        // Reload notifications after creating test data
        loadNotifications();
      }
    } catch (error) {
      console.error('Error creating test notifications:', error);
    }
  }, [loadNotifications]);

  // Load data on component mount
  useEffect(() => {
    loadNotifications();
    loadUsers();
    loadSystemNotifications();
    createTestNotifications();
  }, [loadNotifications, loadUsers, loadSystemNotifications, createTestNotifications]);

  // 실시간 모니터링 자동 업데이트 (최적화)
  useEffect(() => {
    if (!isMonitoringActive) return;

    const interval = setInterval(() => {
      // 모니터링이 활성화된 경우에만 업데이트
      if (isMonitoringActive) {
        loadNotifications();
      }
    }, 300000); // 5분마다 업데이트

    return () => clearInterval(interval);
  }, [isMonitoringActive]); // loadNotifications 의존성 제거로 무한 루프 방지

  const [selectedHistoryIds, setSelectedHistoryIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    setSelectedHistoryIds((prev) => {
      const existing = new Set(notifications.map((n) => n.id));
      const next = prev.filter((id) => existing.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [notifications]);

  const toggleHistorySelection = (notificationId) => {
    setSelectedHistoryIds((prev) =>
      prev.includes(notificationId) ? prev.filter((id) => id !== notificationId) : [...prev, notificationId]
    );
  };

  const toggleSelectAllHistory = () => {
    setSelectedHistoryIds((prev) =>
      prev.length === notifications.length ? [] : notifications.map((n) => n.id)
    );
  };

  const deleteNotificationsByIds = async (ids) => {
    const BATCH_LIMIT = 450;
    for (let i = 0; i < ids.length; i += BATCH_LIMIT) {
      const batch = writeBatch(db);
      ids.slice(i, i + BATCH_LIMIT).forEach((id) => {
        batch.delete(doc(db, `artifacts/${appId}/notifications`, id));
      });
      await batch.commit();
    }
  };

  const handleBulkDeleteNotifications = (mode) => {
    const ids = mode === 'all' ? notifications.map((n) => n.id) : selectedHistoryIds;
    if (ids.length === 0) {
      showToastMessage('삭제할 알림을 선택해 주세요.', 'warning');
      return;
    }
    Modal.confirm({
      title: mode === 'all' ? '알림 기록 전체 삭제' : '선택한 알림 삭제',
      content:
        mode === 'all'
          ? `알림 기록 ${ids.length}건을 모두 삭제합니다. 삭제 후에는 되돌릴 수 없습니다.`
          : `선택한 알림 ${ids.length}건을 삭제합니다. 삭제 후에는 되돌릴 수 없습니다.`,
      okText: '삭제',
      cancelText: '취소',
      okType: 'danger',
      onOk: async () => {
        setBulkDeleting(true);
        try {
          await deleteNotificationsByIds(ids);
          const removed = new Set(ids);
          setNotifications((prev) => prev.filter((n) => !removed.has(n.id)));
          setSelectedHistoryIds([]);
          setCurrentPage(1);
          await loadNotifications();
          showToastMessage(`알림 ${ids.length}건을 삭제했습니다.`, 'success');
        } catch (error) {
          console.error('❌ Bulk delete notifications failed:', error);
          showToastMessage('알림 삭제에 실패했습니다: ' + error.message, 'error');
        } finally {
          setBulkDeleting(false);
        }
      },
    });
  };

  // Delete notification
  const handleDeleteNotification = async (notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);
    
    Modal.confirm({
      title: 'Delete Notification',
      content: `Are you sure you want to delete "${notification?.title || 'Unknown'}" notification?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      okType: 'danger',
      onOk: async () => {
        try {
          console.log('🗑️ Deleting notification:', notificationId);
          
          // Delete from Firestore
          await deleteDoc(doc(db, `artifacts/${appId}/notifications`, notificationId));
          
          // Update local state immediately
          setNotifications(prev => {
            const updated = prev.filter(n => n.id !== notificationId);
            console.log('📊 Updated notifications count:', updated.length);
            return updated;
          });
          
          // Refresh notifications to ensure consistency
          await loadNotifications();
          
          showToastMessage('Notification deleted successfully!', 'success');
        } catch (error) {
          console.error('❌ Error deleting notification:', error);
          showToastMessage('Failed to delete notification: ' + error.message, 'error');
        }
      }
    });
  };

  // Resend notification
  const handleResendNotification = async (notification) => {
    try {
      console.log('🔄 Resending notification:', notification);
      setLoading(true);
      
      // Show confirmation modal
      Modal.confirm({
        title: 'Resend Notification',
        content: `Are you sure you want to resend "${notification.title || 'Unknown'}" notification?`,
        okText: 'Resend',
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            // Prepare notification data for resending
            const notificationData = {
              title: notification.title,
              message: notification.message || notification.body,
              type: notification.type || 'normal',
              sender: notification.sender || 'admin',
              targetType: notification.targetType || 'all',
              targetUsers: notification.targetUsers || (notification.targetType === 'specific_users' ? [] : undefined),
              sendPushNotification: true
            };

            // 특정 사용자 타겟팅인 경우 targetUsers 복원 시도
            if (notificationData.targetType === 'specific_users' && (!notificationData.targetUsers || notificationData.targetUsers.length === 0)) {
              console.log('🔍 특정 사용자 타겟팅이지만 targetUsers가 비어있음. 전체 사용자로 대체합니다.');
              notificationData.targetType = 'all';
              delete notificationData.targetUsers;
            }

            console.log('📤 Resending notification with data:', notificationData);

            // 백엔드 API 연결 테스트
            try {
              const testResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/health`, {
                method: 'GET',
                mode: 'cors', // 🔥 CORS 모드 명시
                credentials: 'include' // 🔥 인증 정보 포함
              });
              console.log('🔍 Backend API health check:', testResponse.ok ? 'OK' : 'FAILED');
            } catch (testError) {
              console.warn('⚠️ Backend API health check failed:', testError.message);
            }

            // FCM 토큰 확인
            console.log('🔍 FCM 토큰 확인 중...');
            const allTokens = await adminNotificationService.getAllUserFcmTokens();
            console.log(`📱 사용 가능한 FCM 토큰 수: ${allTokens.length}`);
            if (allTokens.length === 0) {
              console.warn('⚠️ FCM 토큰이 없습니다. 사용자가 알림 권한을 허용하지 않았을 수 있습니다.');
            } else {
              console.log('📱 FCM 토큰 샘플:', allTokens.slice(0, 2).map(t => ({
                userId: t.userId,
                token: t.token.substring(0, 20) + '...',
                platform: t.platform,
                locale: t.locale
              })));
            }

            // Send notification using existing service
            const result = await adminNotificationService.sendNotification({
              ...notificationData,
              id: notification.id
            });

            console.log('📊 Resend result:', JSON.stringify(result, null, 2));
            console.log('🔍 Resend result details:', {
              success: result.success,
              sentCount: result.sentCount,
              failureCount: result.failureCount,
              successCount: result.successCount,
              reason: result.reason,
              error: result.error,
              message: result.message
            });

            // 성공 조건을 더 관대하게 설정
            if (result.success || result.sentCount > 0 || (result.successCount && result.successCount > 0)) {
              // Update notification status
              const notificationRef = doc(db, `artifacts/${appId}/notifications`, notification.id);
              await updateDoc(notificationRef, {
                status: 'sent',
                sentAt: serverTimestamp(),
                sentCount: result.sentCount,
                failureCount: result.failureCount,
                updatedAt: serverTimestamp()
              });

              // Update local state
              setNotifications(prev => prev.map(n => 
                n.id === notification.id 
                  ? { ...n, status: 'sent', sentAt: new Date(), sentCount: result.sentCount, failureCount: result.failureCount }
                  : n
              ));

              const successCount = result.sentCount || result.successCount || 0;
              showToastMessage(`✅ Notification resent successfully to ${successCount} users!`, "success");
            } else {
              // Update notification status to failed
              const notificationRef = doc(db, `artifacts/${appId}/notifications`, notification.id);
              await updateDoc(notificationRef, {
                status: 'failed',
                errorMessage: result.reason || result.error || 'Resend failed',
                sentCount: 0,
                failureCount: result.failureCount || 0,
                updatedAt: serverTimestamp()
              });

              // Update local state
              setNotifications(prev => prev.map(n => 
                n.id === notification.id 
                  ? { ...n, status: 'failed', errorMessage: result.reason || result.error || 'Resend failed', sentCount: 0, failureCount: result.failureCount || 0 }
                  : n
              ));

              const errorMessage = result.reason || result.error || result.message || 'Unknown error';
              console.error('❌ Resend failed details:', {
                result: JSON.stringify(result, null, 2),
                errorMessage,
                notificationId: notification.id
              });
              showToastMessage(`❌ Failed to resend notification: ${errorMessage}`, "error");
            }

            // Refresh notifications
            loadNotifications();
          } catch (error) {
            console.error('❌ Error resending notification:', error);
            showToastMessage(`❌ Error resending notification: ${error.message}`, "error");
          } finally {
            setLoading(false);
          }
        }
      });
    } catch (error) {
      console.error('❌ Error in handleResendNotification:', error);
      showToastMessage(`❌ Error resending notification: ${error.message}`, "error");
      setLoading(false);
    }
  };

  // Bulk resend pending notifications
  const handleBulkResendPending = async () => {
    try {
      const pendingNotifications = notifications.filter(n => 
        n.status === 'pending' || n.status === 'failed' || !n.status
      );

      if (pendingNotifications.length === 0) {
        showToastMessage("No pending notifications to resend.", "info");
        return;
      }

      Modal.confirm({
        title: 'Bulk Resend Pending Notifications',
        content: `Are you sure you want to resend ${pendingNotifications.length} pending/failed notifications?`,
        okText: 'Resend All',
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            setLoading(true);
            let successCount = 0;
            let failureCount = 0;

            showToastMessage(`🔄 Resending ${pendingNotifications.length} notifications...`, "info");

            for (const notification of pendingNotifications) {
              try {
                const notificationData = {
                  title: notification.title,
                  message: notification.message || notification.body,
                  type: notification.type || 'normal',
                  sender: notification.sender || 'admin',
                  targetType: notification.targetType || 'all',
                  targetUsers: notification.targetUsers || (notification.targetType === 'specific_users' ? [] : undefined),
                  sendPushNotification: true
                };

                // 특정 사용자 타겟팅인 경우 targetUsers 복원 시도
                if (notificationData.targetType === 'specific_users' && (!notificationData.targetUsers || notificationData.targetUsers.length === 0)) {
                  console.log('🔍 특정 사용자 타겟팅이지만 targetUsers가 비어있음. 전체 사용자로 대체합니다.');
                  notificationData.targetType = 'all';
                  delete notificationData.targetUsers;
                }

                const result = await adminNotificationService.sendNotification({
                  ...notificationData,
                  id: notification.id
                });

                if (result.success || result.sentCount > 0) {
                  // Update notification status
                  const notificationRef = doc(db, `artifacts/${appId}/notifications`, notification.id);
                  await updateDoc(notificationRef, {
                    status: 'sent',
                    sentAt: serverTimestamp(),
                    sentCount: result.sentCount,
                    failureCount: result.failureCount,
                    updatedAt: serverTimestamp()
                  });

                  successCount++;
                } else {
                  // Update notification status to failed
                  const notificationRef = doc(db, `artifacts/${appId}/notifications`, notification.id);
                  await updateDoc(notificationRef, {
                    status: 'failed',
                    errorMessage: result.reason || result.error || 'Bulk resend failed',
                    sentCount: 0,
                    failureCount: result.failureCount || 0,
                    updatedAt: serverTimestamp()
                  });

                  failureCount++;
                }
              } catch (error) {
                console.error(`❌ Error resending notification ${notification.id}:`, error);
                failureCount++;
              }
            }

            // Update local state
            setNotifications(prev => prev.map(n => {
              const pendingNotification = pendingNotifications.find(p => p.id === n.id);
              if (pendingNotification) {
                return { ...n, status: 'sent', sentAt: new Date() };
              }
              return n;
            }));

            // Refresh notifications
            loadNotifications();

            if (successCount > 0) {
              showToastMessage(`✅ Successfully resent ${successCount} notifications! ${failureCount > 0 ? `${failureCount} failed.` : ''}`, "success");
            } else {
              showToastMessage(`❌ Failed to resend all ${pendingNotifications.length} notifications.`, "error");
            }
          } catch (error) {
            console.error('❌ Error in bulk resend:', error);
            showToastMessage(`❌ Error in bulk resend: ${error.message}`, "error");
          } finally {
            setLoading(false);
          }
        }
      });
    } catch (error) {
      console.error('❌ Error in handleBulkResendPending:', error);
      showToastMessage(`❌ Error in bulk resend: ${error.message}`, "error");
    }
  };

  // Create notification
  const handleCreateNotification = async (values) => {
    try {
      console.log('🚀 handleCreateNotification 시작:', values);
      setLoading(true);
      
      // targetType mapping (matches AdminNotificationService)
      const targetType = values.targetType; // Use 'all', 'inactive_users', 'specific_users' as is
      
      // 메시지 내용 정리 로직 제거 - 사용자가 입력한 내용을 그대로 사용
      const cleanMessage = values.message || '';
      
      // 제목에서 중복 이모지 제거
      const cleanTitle = values.title ? values.title.replace(/^[📢🔊🚨📋🛡️🏛️💰]+/, '') : '';
      
      const notificationData = {
        title: cleanTitle,
        body: cleanMessage,
        message: cleanMessage, // body와 message 모두 설정
        type: values.type,
        sender: values.sender,
        targetType: targetType,
        targetUsers: values.targetUsers || [],
        read: false, // 기본적으로 읽지 않은 상태로 설정
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // status will be set based on push notification dispatch result (no initial value)
        createdBy: user.uid,
        targetCount: targetType === 'all' ? users.length : 
                    values.targetUsers?.length || 0
      };
      
      console.log('Creating notification with data:', notificationData);

      // Set targetUsers (use selectedUserIds for specific_users, ensure consistency)
      const finalTargetUsers = values.targetType === 'specific_users' ? 
        (selectedUserIds.length > 0 ? selectedUserIds : values.targetUsers || []) : 
        []; // For 'all' and 'inactive_users', targetUsers should be empty

      // Save to Firestore
      const docRef = await addDoc(collection(db, `artifacts/${appId}/notifications`), notificationData);
      
      // Create individual notifications for target users
      let targetUserIds = [];
      if (values.targetType === 'specific_users') {
        targetUserIds = finalTargetUsers;
      } else if (values.targetType === 'all') {
        targetUserIds = users.map(user => user.id);
      } else if (values.targetType === 'inactive_users') {
        targetUserIds = inactiveUsers.map(user => user.id);
      } else if (values.targetType === 'policy_compliance_low') {
        // Policy Compliance가 50% 이상 100% 미만인 사용자들
        const usersWithCompliance = await Promise.all(
          users.map(async (user) => {
            const firebaseUtils = { db, collection, getDocs, query, where };
            const complianceInfo = await calculatePolicyCompliance(user.id, appId, firebaseUtils);
            return { ...user, compliance: complianceInfo.compliance };
          })
        );
        targetUserIds = usersWithCompliance
          .filter(user => user.compliance >= 50 && user.compliance < 100)
          .map(user => user.id);
      } else if (values.targetType === 'policy_compliance_critical') {
        // Policy Compliance가 50% 미만인 사용자들
        const usersWithCompliance = await Promise.all(
          users.map(async (user) => {
            const firebaseUtils = { db, collection, getDocs, query, where };
            const complianceInfo = await calculatePolicyCompliance(user.id, appId, firebaseUtils);
            return { ...user, compliance: complianceInfo.compliance };
          })
        );
        targetUserIds = usersWithCompliance
          .filter(user => user.compliance < 50)
          .map(user => user.id);
      } else if (values.targetType === 'no_employment_data') {
        // 고용현황 데이터가 없는 사용자들
        const usersWithEmployment = await Promise.all(
          users.map(async (user) => {
            const employmentsRef = collection(db, 'artifacts', appId, 'users', user.id, 'employments');
            const employmentsSnapshot = await getDocs(employmentsRef);
            return { ...user, hasEmploymentData: !employmentsSnapshot.empty };
          })
        );
        targetUserIds = usersWithEmployment
          .filter(user => !user.hasEmploymentData)
          .map(user => user.id);
      }
      
      await createIndividualUserNotifications(docRef.id, notificationData, values, targetUserIds);
      
      // Send push notification if enabled
      console.log('🔍 푸시 알림 발송 체크:', {
        sendPushNotification: values.sendPushNotification,
        type: typeof values.sendPushNotification,
        targetUserIdsCount: targetUserIds.length
      });
      
      if (values.sendPushNotification) {
        console.log('✅ 푸시 알림 발송 활성화됨 - 발송 시작');
        console.log('Creating notification...');
        console.log('Form data:', {
          title: values.title,
          message: values.message,
          type: values.type,
          sender: values.sender,
          targetType: values.targetType,
          targetUsers: values.targetUsers
        });
        
        // 전송 시작 메시지 표시
        const targetCount = values.targetType === 'all' ? users.length : 
                           values.targetType === 'specific_users' ? finalTargetUsers.length : 
                           values.targetType === 'inactive_users' ? inactiveUsers.length :
                           targetUserIds.length;
        showToastMessage(`📤 Sending push notifications to ${targetCount} users...`, "info");
        
        console.log('🔍 타겟 전송 디버깅 정보:');
        console.log('- targetType:', values.targetType);
        console.log('- selectedUserIds:', selectedUserIds);
        console.log('- selectedUserIds.length:', selectedUserIds.length);
        console.log('- values.targetUsers:', values.targetUsers);
        console.log('- finalTargetUsers:', finalTargetUsers);
        console.log('- finalTargetUsers.length:', finalTargetUsers.length);
        
        console.log('Sending notification with data:', {
          ...values,
          id: docRef.id,
          targetUsers: finalTargetUsers
        });
        
        console.log('🚀 ===== 푸시알림 발송 시작 =====');
        console.log('📋 발송 데이터:', {
          title: values.title,
          message: values.message,
          type: values.type,
          sender: values.sender,
          targetType: values.targetType,
          targetUsers: finalTargetUsers,
          targetCount: finalTargetUsers.length
        });
        
        try {
          console.log('📞 adminNotificationService.sendNotification 호출 시작...');
          const result = await adminNotificationService.sendNotification({
            ...values,
            id: docRef.id,
            targetUsers: finalTargetUsers // Pass correct user ID array
          });
          console.log('📞 adminNotificationService.sendNotification 호출 완료');
        
          console.log('📊 ===== 푸시알림 발송 결과 =====');
          console.log('✅ 성공 여부:', result.success);
          console.log('📤 발송 성공 수:', result.sentCount);
          console.log('❌ 발송 실패 수:', result.failureCount);
          console.log('🔍 실패 사유:', result.reason);
          console.log('⚠️ 오류 메시지:', result.error);
          console.log('📋 전체 결과:', result);
        
          // FCM 발송 결과 상세 로깅
          console.log('🔍 FCM 발송 결과 분석:');
          console.log('- success:', result.success);
          console.log('- sentCount:', result.sentCount);
          console.log('- failureCount:', result.failureCount);
          console.log('- reason:', result.reason);
          console.log('- error:', result.error);
          console.log('Full result object keys:', Object.keys(result));
          
          // Check if notification was sent successfully (even if some failed)
          const hasSuccessfulSends = result.sentCount > 0 || result.successCount > 0;
          const hasFailures = result.failureCount > 0;
        
          if (result.success || hasSuccessfulSends) {
            await updateDoc(docRef, {
              status: 'sent',
              sentAt: serverTimestamp(),
              sentCount: result.sentCount,
              failureCount: result.failureCount
            });
            console.log('✅ Push notification sent successfully:', result.sentCount, 'users');
            
            // Update monitoring data
            await updateSendMonitoringData();
            
            // Update local state immediately
            setNotifications(prev => prev.map(n => 
              n.id === docRef.id 
                ? { ...n, status: 'sent', sentAt: new Date(), sentCount: result.sentCount, failureCount: result.failureCount }
                : n
            ));
            
            // Show success message and close modal
            if (hasFailures) {
              showToastMessage(`✅ Push notifications successfully sent to ${result.sentCount || result.successCount} users!`, "success");
            } else {
              showToastMessage(`✅ Push notifications successfully sent to ${result.sentCount || result.successCount} users!`, "success");
            }
            
            // 성공 시 폼 초기화
            form.resetFields();
            setSelectedUserIds([]);
            setNotificationForm({
              title: "",
              message: "",
              type: "normal",
              priority: "medium",
              sender: "admin",
              targetType: "all",
              targetUsers: [],
              scheduledAt: null,
              isScheduled: false,
              sendPushNotification: true,
              requireInteraction: false
            });
            loadNotifications();
          } else {
            // Only mark as failed if no users were successfully notified
            await updateDoc(docRef, {
              status: 'failed',
              errorMessage: result.reason || result.error || 'No users were notified',
              sentCount: 0,
              failureCount: result.failureCount || 0
            });
            console.error('❌ Push notification completely failed:', result.reason || result.error);
            
            // Update monitoring data
            await updateSendMonitoringData();
            
            // Update local state immediately
            setNotifications(prev => prev.map(n => 
              n.id === docRef.id 
                ? { ...n, status: 'failed', errorMessage: result.reason || result.error || 'No users were notified', sentCount: 0, failureCount: result.failureCount || 0 }
                : n
            ));
            
            // 명확한 실패 메시지 표시
            const errorMessage = result.reason || result.error || 'Unknown error';
            if (errorMessage.includes('fetch') || errorMessage.includes('network') || errorMessage.includes('ECONNREFUSED')) {
              showToastMessage(`❌ Cannot connect to backend server. Please check server status.`, "error");
            } else if (errorMessage.includes('no_tokens')) {
              showToastMessage(`⚠️ No FCM tokens available for sending. Please ensure users are logged into the app.`, "warning");
            } else {
              showToastMessage(`❌ Failed to send push notifications: ${errorMessage}`, "error");
            }
            
            // 실패 시에도 폼은 유지 (사용자가 다시 시도할 수 있도록)
            setSelectedUserIds([]);
            setNotificationForm({
              title: "",
              message: "",
              type: "normal",
              priority: "medium",
              sender: "admin",
              targetType: "all",
              targetUsers: [],
              scheduledAt: null,
              isScheduled: false,
              sendPushNotification: true,
              requireInteraction: false
            });
            loadNotifications();
          }
        } catch (pushError) {
          console.error('❌ 푸시 알림 발송 중 예외 발생:', pushError);
          console.error('❌ 에러 상세:', {
            message: pushError.message,
            stack: pushError.stack,
            name: pushError.name
          });
          showToastMessage(`❌ 푸시 알림 발송 중 오류가 발생했습니다: ${pushError.message}`, "error");
        }
      } else {
        console.warn('⚠️ 푸시 알림 발송이 비활성화되어 있습니다 (sendPushNotification: false)');
        // When not sending push notifications
        showToastMessage("✅ Notification created successfully.", "success");
        // 푸시알림을 보내지 않는 경우에도 폼 초기화
        form.resetFields();
        setSelectedUserIds([]);
        setNotificationForm({
          title: "",
          message: "",
          type: "normal",
          priority: "medium",
          sender: "admin",
          targetType: "all",
          targetUsers: [],
          scheduledAt: null,
          isScheduled: false,
          sendPushNotification: true,
          requireInteraction: false
        });
        loadNotifications();
      }
      
    } catch (error) {
      console.error("Error creating notification:", error);
      showToastMessage("❌ 알림 생성에 실패했습니다. 다시 시도해주세요.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Test notification
  const handleTestNotification = async (type = 'normal') => {
    try {
      const result = await mobileNotificationTester.sendTestNotification(type);
      if (result) {
        showToastMessage(`${type} test notification sent successfully.`, "success");
      } else {
        showToastMessage(`${type} test notification failed to send.`, "error");
      }
    } catch (error) {
      console.error("Test notification error:", error);
      showToastMessage("Test notification failed to send.", "error");
    }
  };

  // Handle user selection
  const handleUserSelection = (checkedValues) => {
    setSelectedUserIds(checkedValues);
  };

  // Toggle user selection
  const toggleUserSelection = (userId) => {
    console.log('🔄 사용자 선택 토글:', userId);
    setSelectedUserIds(prev => {
      const isSelected = prev.includes(userId);
      const newSelection = isSelected 
        ? prev.filter(id => id !== userId)
        : [...prev, userId];
      console.log('📊 새로운 선택 목록:', newSelection);
      return newSelection;
    });
  };

  // Create individual notifications for each target user
  const createIndividualUserNotifications = async (notificationId, notificationData, values, targetUsers) => {
    try {
      console.log('📝 개별 사용자 알림 생성 시작:', targetUsers.length, '명');
      console.log('📝 타겟 사용자 목록:', targetUsers);
      console.log('📝 알림 데이터:', notificationData);
      
      if (!targetUsers || targetUsers.length === 0) {
        console.log('📝 타겟 사용자가 없어서 개별 알림을 생성하지 않습니다.');
        return;
      }

      const batch = [];
      const { writeBatch } = await import('firebase/firestore');
      const batchRef = writeBatch(db);

      for (const userId of targetUsers) {
        // 각 사용자별 개별 알림 생성 - 원본 알림 ID를 문서 ID로 사용
        const userNotificationRef = doc(db, `artifacts/${appId}/users/${userId}/notifications`, notificationId);
        
        const userNotificationData = {
          id: notificationId, // 원본 알림 ID 참조
          title: notificationData.title,
          message: notificationData.body, // body를 message로 매핑
          type: notificationData.type,
          priority: values.priority || 'medium', // values에서 priority 가져오기
          sender: notificationData.sender,
          targetType: notificationData.targetType,
          targetUsers: [userId], // 개별 사용자만 포함
          read: false,
          createdAt: notificationData.createdAt,
          createdBy: notificationData.createdBy,
          status: 'delivered', // 개별 알림은 delivered 상태로 시작
          isIndividualNotification: true, // 개별 알림임을 표시
          originalNotificationId: notificationId // 원본 알림 ID 참조
        };

        console.log(`📝 사용자 ${userId}에게 개별 알림 생성 중:`, {
          path: `artifacts/${appId}/users/${userId}/notifications/${notificationId}`,
          data: userNotificationData
        });

        batchRef.set(userNotificationRef, userNotificationData);
        console.log(`📝 사용자 ${userId}에게 개별 알림 생성:`, userNotificationData.title);
      }

      console.log('📝 배치 커밋 시작...');
      await batchRef.commit();
      console.log(`✅ ${targetUsers.length}명의 사용자에게 개별 알림 생성 완료`);
      
    } catch (error) {
      console.error('❌ 개별 사용자 알림 생성 실패:', error);
      console.error('❌ 에러 상세 정보:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      // 개별 알림 생성 실패는 전체 프로세스를 중단시키지 않음
    }
  };

  // 새 알림 생성 시 상태 초기화 함수
  const handleCreateNewNotification = () => {
    // 페이지 스크롤을 즉시 맨 위로 이동
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // 모든 상태 초기화
    setSelectedUserIds([]);
    setNotificationForm({
      title: "",
      message: "",
      type: "normal",
      priority: "medium",
      sender: "admin",
      targetType: "all",
      targetUsers: [],
      scheduledAt: null,
      isScheduled: false,
      sendPushNotification: true,
      requireInteraction: false
    });
    form.resetFields();
  };


  // User Selection Modal 상태 추적
  useEffect(() => {
    console.log('🎯 User Selection Modal 상태 변화:', isUserSelectionModalVisible);
  }, [isUserSelectionModalVisible]);

  // Open user selection modal
  const openUserSelectionModal = () => {
    console.log('🔍 User Selection Modal 열기 시도');
    console.log('📊 현재 isUserSelectionModalVisible:', isUserSelectionModalVisible);
    
    // 기존 선택된 사용자가 있다면 유지, 없다면 초기화
    if (notificationForm.selectedUsersInfo && notificationForm.selectedUsersInfo.length > 0) {
      setSelectedUserIds(notificationForm.targetUsers || []);
      console.log('🔄 기존 선택된 사용자 유지:', notificationForm.targetUsers);
    } else {
      setSelectedUserIds([]);
      setUserSearchTerm("");
      setUserSearchResults([]);
      setSearchFilters({
        hasRemittance: "",
        remittanceDays: "",
        userStatus: 'all'
      });
    }
    
    // 초기 로딩 제거 - 검색을 통해서만 사용자 선택
    setIsUserSelectionModalVisible(true);
    console.log('✅ setIsUserSelectionModalVisible(true) 호출 완료');
  };

  // Confirm user selection
  const confirmUserSelection = () => {
    console.log('✅ 사용자 선택 확인:', selectedUserIds);
    console.log('📊 선택된 사용자 수:', selectedUserIds.length);
    
    // 선택된 사용자 정보를 가져와서 표시용으로 저장
    const selectedUsersInfo = userSearchResults.filter(user => 
      selectedUserIds.includes(user.id)
    );
    
    setNotificationForm(prev => ({
      ...prev,
      targetType: 'specific_users',
      targetUsers: selectedUserIds,
      selectedUsersInfo: selectedUsersInfo // 선택된 사용자 정보 저장
    }));
    
    setIsUserSelectionModalVisible(false);
    
    // 더 명확한 피드백 메시지
    if (selectedUserIds.length > 0) {
      showToastMessage(`✅ ${selectedUserIds.length} user(s) selected. Check the notification form.`, "success");
    } else {
      showToastMessage('⚠️ Please select at least one user.', "warning");
    }
  };

  // FCM 푸시 알림 테스트 함수
  const testFCMNotification = async () => {
    try {
      console.log('🧪 FCM 푸시 알림 테스트 시작');

      if (selectedUserIds.length === 0) {
        showToastMessage('먼저 사용자를 선택해주세요.', "warning");
        return;
      }

      const testNotificationData = {
        title: 'FCM 테스트 알림',
        message: '특정 사용자 선택 기능이 정상적으로 작동합니다.',
        type: 'normal',
        sender: 'admin',
        targetType: 'specific_users',
        targetUsers: selectedUserIds,
        sendPushNotification: true
      };

      console.log('🧪 테스트 알림 데이터:', testNotificationData);
      showToastMessage('🧪 FCM 테스트를 시작합니다...', "info");

      const result = await adminNotificationService.sendNotification(testNotificationData);

      console.log('🧪 FCM 테스트 결과:', result);

      if (result.success) {
        showToastMessage(`✅ FCM 테스트 성공! ${result.sentCount}명에게 전송됨`, "success");
      } else {
        const errorMessage = result.reason || result.error || '알 수 없는 오류';
        if (errorMessage.includes('fetch') || errorMessage.includes('network') || errorMessage.includes('ECONNREFUSED')) {
          showToastMessage(`❌ FCM 테스트 실패: 백엔드 서버에 연결할 수 없습니다.`, "error");
        } else if (errorMessage.includes('no_tokens')) {
          showToastMessage(`⚠️ FCM 테스트 실패: 선택된 사용자들의 FCM 토큰이 없습니다.`, "warning");
        } else {
          showToastMessage(`❌ FCM 테스트 실패: ${errorMessage}`, "error");
        }
      }

    } catch (error) {
      console.error('🧪 FCM 테스트 오류:', error);
      if (error.message.includes('fetch') || error.message.includes('network')) {
        showToastMessage(`❌ FCM 테스트 오류: 백엔드 서버에 연결할 수 없습니다.`, "error");
      } else {
        showToastMessage(`❌ FCM 테스트 오류: ${error.message}`, "error");
      }
    }
  };

  // Helper function to format dates (optimized - reduced logging)
  const formatDate = (date) => {
    if (!date) {
      return 'Unknown';
    }
    
    // Only log in development mode and for major state changes
    if (import.meta.env.DEV && Math.random() < 0.1) { // 10% chance to log
      console.log('formatDate input:', date, 'type:', typeof date, 'isDate:', date instanceof Date);
    }
    
    if (date instanceof Date) {
      const result = date.toLocaleDateString();
      if (import.meta.env.DEV && Math.random() < 0.1) {
        console.log('formatDate result (Date):', result);
      }
      return result;
    }
    
    if (date.toDate && typeof date.toDate === 'function') {
      const result = date.toDate().toLocaleDateString();
      if (import.meta.env.DEV && Math.random() < 0.1) {
        console.log('formatDate result (toDate):', result);
      }
      return result;
    }
    
    try {
      const result = new Date(date).toLocaleDateString();
      if (import.meta.env.DEV && Math.random() < 0.1) {
        console.log('formatDate result (new Date):', result);
      }
      return result;
    } catch (error) {
      console.error('formatDate error:', error, 'input:', date);
      return 'Invalid Date';
    }
  };

  // Optimized user search function with admin filtering
  const searchUsers = useCallback(async (searchTerm, filters = {}) => {
    if (!searchTerm || searchTerm.length < 2) {
      setUserSearchResults([]);
      return;
    }

    setIsSearchingUsers(true);
    try {
      console.log('🔍 사용자 검색 시작:', searchTerm, 'filters:', filters);
      console.log('🔍 현재 Admin 사용자 정보 (검색):', {
        uid: user?.uid,
        email: user?.email,
        role: user?.role,
        admin: user?.admin,
        customClaims: user?.customClaims
      });
      
      // Admin 권한 확인
      const hasAdminPermission = checkAdminPermissions();
      console.log('🔍 Admin 권한 확인 결과 (검색):', hasAdminPermission);
      
      const hardcodedAppId = 'kairos-platform-1ffde';
      const allUsers = [];
      const processedUserIds = new Set();
      
      // 메인 경로에서만 검색 (어드민 제외)
      const userCollectionPaths = [
        `artifacts/${hardcodedAppId}/users`  // 메인 경로만 사용
      ];
      
      // 각 경로에서 사용자 정보 수집 (검색 최적화)
      for (const collectionPath of userCollectionPaths) {
        try {
          console.log(`📍 경로 검색: ${collectionPath}`);
          const usersRef = collection(db, collectionPath);
          
          // 검색을 위한 쿼리 (최대 100명으로 제한)
          const usersQuery = query(
            usersRef,
            orderBy('createdAt', 'desc'),
            limit(100)
          );
          
          const snapshot = await getDocs(usersQuery);
          console.log(`📊 ${collectionPath} 문서 수:`, snapshot.docs.length);
          
          for (const userDoc of snapshot.docs) {
            const userData = userDoc.data();
            const userId = userDoc.id;
            
            // 어드민 사용자 제외
            const isAdmin = userData.role === 'admin' || 
                           userData.admin === true || 
                           userData.isAdmin === true ||
                           (userData.customClaims && userData.customClaims.admin === true);
            
            if (isAdmin) {
              console.log(`🚫 어드민 사용자 제외: ${userId} (${userData.email})`);
              continue;
            }
            
            // 이미 처리된 사용자인지 확인
            if (processedUserIds.has(userId)) {
              continue;
            }
            
            console.log(`👤 일반 사용자 발견: ${userId} (${collectionPath})`);
            
            // 기본 사용자 정보 구성
            const userInfo = {
              id: userId,
              ...userData,
              name: userData.displayName || userData.name || userData.fullName || userData.email?.split('@')[0] || '이름 없음',
              displayName: userData.displayName || userData.name || userData.fullName || userData.email?.split('@')[0] || '이름 없음',
              phone: userData.phone || userData.phoneNumber || '',
              lastActivity: userData.lastActivity?.toDate?.() || new Date(),
            };
            
            allUsers.push(userInfo);
            processedUserIds.add(userId);
            console.log(`✅ 일반 사용자 추가 완료: ${userInfo.email || userId}`);
          }
        } catch (error) {
          console.warn(`❌ ${collectionPath} 로드 실패:`, error.message);
          // 개별 경로 실패는 전체를 중단시키지 않음
        }
      }
      
      console.log('✅ 최종 사용자 수집 결과:');
      console.log(`📊 총 발견된 사용자: ${allUsers.length}명`);

      // Load remittance data for filtering
      const remittancesRef = collection(db, `artifacts/${hardcodedAppId}/remittances`);
      const remittancesSnapshot = await getDocs(remittancesRef);
      
      const userLastRemittance = new Map();
      remittancesSnapshot.docs.forEach(doc => {
        const remittanceData = doc.data();
        const userId = remittanceData.userId || remittanceData.user_id;
        const remittanceDate = remittanceData.createdAt?.toDate?.() || 
                              remittanceData.timestamp?.toDate?.() || 
                              remittanceData.date?.toDate?.() ||
                              new Date(remittanceData.createdAt || remittanceData.timestamp || remittanceData.date);
        
        if (userId) {
          const existingDate = userLastRemittance.get(userId);
          if (!existingDate || remittanceDate > existingDate) {
            userLastRemittance.set(userId, remittanceDate);
          }
        }
      });

      // Add search conditions based on search term
      const searchLower = searchTerm.toLowerCase();

      // Filter users based on search term and filters
      const filteredUsers = allUsers.filter(user => {
        // Text search
        const userName = user.displayName || user.name || user.fullName || user.email?.split('@')[0] || '';
        const userEmail = user.email?.toLowerCase() || '';
        const userId = user.id.toLowerCase();
        
        console.log(`🔍 사용자 검색 비교:`, {
          searchTerm: searchTerm,
          searchLower: searchLower,
          userName: userName,
          userEmail: userEmail,
          userId: userId,
          displayName: user.displayName,
          name: user.name,
          fullName: user.fullName
        });
        
        // 더 유연한 검색: 이메일의 로컬 부분만 비교하거나 전체 이메일 비교
        const emailLocalPart = userEmail.split('@')[0];
        const searchEmailLocalPart = searchLower.split('@')[0];
        
        const matchesSearch = 
          userName.toLowerCase().includes(searchLower) ||
          userEmail.includes(searchLower) ||
          emailLocalPart.includes(searchEmailLocalPart) ||
          userId.includes(searchLower) ||
          (user.phoneNumber && user.phoneNumber.includes(searchTerm));

        console.log(`🔍 검색 매칭 결과:`, {
          userNameMatch: userName.toLowerCase().includes(searchLower),
          emailMatch: userEmail.includes(searchLower),
          emailLocalMatch: emailLocalPart.includes(searchEmailLocalPart),
          idMatch: userId.includes(searchLower),
          phoneMatch: user.phoneNumber && user.phoneNumber.includes(searchTerm),
          finalMatch: matchesSearch
        });

        if (!matchesSearch) return false;

        // Apply filters
        const lastRemittance = userLastRemittance.get(user.id);
        
        // Remittance filter
        if (filters.hasRemittance !== "") {
          if (filters.hasRemittance && !lastRemittance) return false;
          if (!filters.hasRemittance && lastRemittance) return false;
        }

        // Days since remittance filter - show users who made remittance within the specified days
        if (filters.remittanceDays !== "" && lastRemittance) {
          const daysSince = Math.floor((new Date() - lastRemittance) / (1000 * 60 * 60 * 24));
          if (daysSince > parseInt(filters.remittanceDays)) return false; // Exclude if remittance was more than N days ago
        }

        // User status filter
        if (filters.userStatus === 'inactive') {
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          if (lastRemittance && lastRemittance >= thirtyDaysAgo) return false;
        } else if (filters.userStatus === 'active') {
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          if (!lastRemittance || lastRemittance < thirtyDaysAgo) return false;
        }

        return true;
      }).map(user => {
        const lastRemittance = userLastRemittance.get(user.id);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        return {
          ...user,
          name: user.displayName || user.name || user.fullName || user.email?.split('@')[0] || '이름 없음',
          displayName: user.displayName || user.name || user.fullName || user.email?.split('@')[0] || '이름 없음',
          phone: user.phone || user.phoneNumber || '',
          lastRemittance: lastRemittance,
          hasRemittance: !!lastRemittance,
          status: lastRemittance && lastRemittance >= thirtyDaysAgo ? 'active' : 'inactive'
        };
      });

      console.log('✅ 검색 결과:', filteredUsers.length, '명의 사용자 발견');
      setUserSearchResults(filteredUsers);
    } catch (error) {
      console.error('❌ 사용자 검색 실패:', error);
      console.error('❌ 오류 상세:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      setUserSearchResults([]);
    } finally {
      setIsSearchingUsers(false);
    }
  }, [appId, checkAdminPermissions]);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((searchTerm, filters) => {
      searchUsers(searchTerm, filters);
    }, 300),
    [searchUsers]
  );

  // Use search results only - no fallback to users list
  const displayUsers = userSearchResults.length > 0 ? userSearchResults : [];

  // Render notification type tag
  const renderNotificationType = (type) => {
    const typeConfig = NOTIFICATION_TYPES[type] || NOTIFICATION_TYPES.normal;
    return (
      <Tag color={typeConfig.color} icon={typeConfig.icon}>
        {typeConfig.label}
      </Tag>
    );
  };

  // Render sender info
  const renderSender = (sender) => {
    const senderConfig = SENDER_OPTIONS.find(s => s.value === sender) || SENDER_OPTIONS[0];
    return (
      <Space>
        <span>{senderConfig.icon}</span>
        <Text>{senderConfig.label}</Text>
      </Space>
    );
  };

  // Render status tag
  const renderStatus = (status) => {
    const statusConfig = {
      pending: { color: "orange", text: "Pending" },
      sent: { color: "green", text: "Sent" },
      failed: { color: "red", text: "Failed" },
      scheduled: { color: "blue", text: "Scheduled" }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // Notification columns
  const notificationColumns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.message?.substring(0, 50)}...
          </Text>
        </Space>
      )
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: renderNotificationType
    },
    {
      title: "Sender",
      dataIndex: "sender",
      key: "sender",
      render: renderSender
    },
    {
      title: "Target",
      dataIndex: "targetType",
      key: "targetType",
      render: (type, record) => {
        const targetText = {
          all: `All Users (${record.targetCount})`,
          specific_users: `Specific Users (${record.targetCount})`,
          inactive_users: `Inactive Users (${record.targetCount})`,
          policy_compliance_low: `Policy Compliance Low (${record.targetCount})`,
          policy_compliance_critical: `Policy Compliance Critical (${record.targetCount})`,
          no_employment_data: `No Employment Data (${record.targetCount})`
        };
        return <Text>{targetText[type] || type}</Text>;
      }
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatus
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => dayjs(date).format("YYYY-MM-DD HH:mm")
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button 
            type="text" 
            icon={<EyeOutlined />} 
            size="small"
            onClick={() => {/* View details */}}
          />
          <Button 
            type="text" 
            icon={<EditOutlined />} 
            size="small"
            onClick={() => {/* Edit notification */}}
          />
          <Button 
            type="text" 
            icon={<DeleteOutlined />} 
            size="small"
            danger
            onClick={() => handleDeleteNotification(notification.id)}
          />
        </Space>
      )
    }
  ];

  // Inactive users columns
  const inactiveUsersColumns = [
    {
      title: "User ID",
      dataIndex: "id",
      key: "id",
      render: (id) => <Text code>{id.substring(0, 8)}...</Text>
    },
    {
      title: "Name",
      dataIndex: "displayName",
      key: "displayName",
      render: (name, record) => <Text>{name || record.name || record.fullName || record.email?.split('@')[0] || `User ${record.id.substring(0, 8)}`}</Text>
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email"
    },
    {
      title: "Last Remittance",
      dataIndex: "lastRemittance",
      key: "lastRemittance",
      render: (date) => date ? dayjs(date).format("YYYY-MM-DD") : <Text type="danger">No remittance</Text>
    },
    {
      title: "Days Since Remittance",
      key: "daysSinceRemittance",
      render: (_, record) => {
        if (!record.lastRemittance) {
          return <Text type="danger">No remittance record</Text>;
        }
        const days = Math.floor((new Date() - new Date(record.lastRemittance)) / (1000 * 60 * 60 * 24));
        return <Text type="danger">{days} days</Text>;
      }
    }
  ];

  return (
    <>
    <div className="min-h-screen bg-slate-50">
      <div className="p-3 space-y-3">
        {/* Push Notification Form - Professional Design */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-slate-200">
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-slate-200">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
                <Bell className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="text-[15px] font-semibold text-slate-900 leading-tight">Send Push Notification</h2>
                <p className="text-[11px] text-slate-500 leading-tight">Push Notification Center</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">
                Total <span className="font-semibold text-slate-900 tabular-nums">{notifications.length}</span>
              </span>
              <button
                onClick={async () => {
                  await loadNotifications();
                  await loadUsers();
                  await loadSystemNotifications();
                }}
                className="w-7 h-7 border border-slate-200 rounded-md flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors"
                title="Refresh Data"
              >
                <SyncOutlined className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Professional Content Area */}
          <div className="p-4 bg-white">
            <Form
              form={form}
              layout="vertical"
              className="space-y-3 compact-push-form"
            >
              {/* Title and Type Section - Enhanced */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <Form.Item
                  label={<span className="text-xs font-semibold text-gray-700">Notification Title <span className="text-red-500">*</span></span>}
                  name="title"
                  rules={[{ required: true, message: "Please enter a title" }]}
                  className="mb-0"
                >
                  <Input
                    placeholder="e.g., Emergency welfare support notice, Remittance service maintenance..."
                    className="w-full h-9 px-3 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    style={{ fontSize: '14px' }}
                  />
                </Form.Item>

                <Form.Item
                  label={<span className="text-xs font-semibold text-gray-700">Notification Type <span className="text-red-500">*</span></span>}
                  name="type"
                  rules={[{ required: true, message: "Please select a notification type" }]}
                  initialValue="normal"
                  className="mb-0"
                >
                  <Radio.Group className="w-full notification-type-radio-group">
                    <div className="grid grid-cols-3 gap-2">
                      <Radio value="normal" className="notification-type-radio">
                        <div className="flex items-center justify-center gap-1.5 h-9 border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all cursor-pointer notification-type-card">
                          <Info className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-gray-700">Normal</span>
                        </div>
                      </Radio>
                      <Radio value="urgent" className="notification-type-radio">
                        <div className="flex items-center justify-center gap-1.5 h-9 border border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition-all cursor-pointer notification-type-card">
                          <AlertCircle className="w-4 h-4 text-red-600" />
                          <span className="text-sm font-medium text-gray-700">Urgent</span>
                        </div>
                      </Radio>
                      <Radio value="warning" className="notification-type-radio">
                        <div className="flex items-center justify-center gap-1.5 h-9 border border-gray-200 rounded-lg hover:border-orange-400 hover:bg-orange-50 transition-all cursor-pointer notification-type-card">
                          <AlertTriangle className="w-4 h-4 text-orange-600" />
                          <span className="text-sm font-medium text-gray-700">Warning</span>
                        </div>
                      </Radio>
                    </div>
                  </Radio.Group>
                  <style dangerouslySetInnerHTML={{__html: `
                    .notification-type-radio-group .ant-radio-wrapper {
                      width: 100%;
                      margin: 0;
                    }
                    .notification-type-radio-group .ant-radio-wrapper .ant-radio {
                      display: none;
                    }
                    .notification-type-radio-group .ant-radio-wrapper > span:last-child {
                      width: 100%;
                      padding: 0;
                    }
                    .compact-push-form .ant-form-item-label {
                      padding-bottom: 4px;
                    }
                    .notification-type-radio-group .ant-radio-wrapper-checked .notification-type-card {
                      border-color: #3b82f6 !important;
                      background-color: #eff6ff !important;
                      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                    }
                    .notification-type-radio-group .ant-radio-wrapper[value="urgent"].ant-radio-wrapper-checked .notification-type-card {
                      border-color: #ef4444 !important;
                      background-color: #fef2f2 !important;
                      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
                    }
                    .notification-type-radio-group .ant-radio-wrapper[value="warning"].ant-radio-wrapper-checked .notification-type-card {
                      border-color: #f97316 !important;
                      background-color: #fff7ed !important;
                      box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
                    }
                  `}} />
                </Form.Item>
              </div>

              {/* Message Section - Enhanced */}
              <Form.Item
                label={<span className="text-xs font-semibold text-gray-700">Message Content <span className="text-red-500">*</span></span>}
                name="message"
                rules={[{ required: true, message: "Please enter a message" }]}
                className="mb-0"
              >
                <textarea
                  rows={3}
                  placeholder="Enter notification message content..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-y"
                  style={{ fontSize: '14px', lineHeight: '1.5' }}
                />
              </Form.Item>

              {/* Sender and Target Section - Enhanced */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <Form.Item
                  label={<span className="text-xs font-semibold text-gray-700">Sender <span className="text-red-500">*</span></span>}
                  name="sender"
                  rules={[{ required: true, message: "Please select a sender" }]}
                  initialValue="admin"
                  className="mb-0"
                >
                  <Select
                    placeholder="Select sender"
                    className="w-full"
                    showSearch
                    optionFilterProp="children"
                    dropdownStyle={{ zIndex: 10003 }}
                    style={{ fontSize: '14px' }}
                  >
                    {SENDER_OPTIONS.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.icon} {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  label={<span className="text-xs font-semibold text-gray-700">Target Users <span className="text-red-500">*</span></span>}
                  name="targetType"
                  rules={[{ required: true, message: "Please select target users" }]}
                  className="mb-0"
                >
                  <Select
                    placeholder="Select target user group"
                    className="w-full"
                    showSearch
                    optionFilterProp="children"
                    dropdownStyle={{ zIndex: 10003 }}
                    style={{ fontSize: '14px' }}
                    onChange={(value) => {
                      if (value === 'specific_users') {
                        if (notificationForm.selectedUsersInfo && notificationForm.selectedUsersInfo.length > 0) {
                          setSelectedUserIds(notificationForm.targetUsers || []);
                        }
                        openUserSelectionModal();
                      } else {
                        setNotificationForm(prev => ({
                          ...prev,
                          selectedUsersInfo: []
                        }));
                      }
                    }}
                  >
                    <Option value="all">
                      <div className="flex items-center space-x-2">
                        <Users className="w-4 h-4 text-blue-600" />
                        <span>All Users</span>
                      </div>
                    </Option>
                    <Option value="inactive_users">
                      <div className="flex items-center space-x-2">
                        <User className="w-4 h-4 text-orange-600" />
                        <span>Inactive Users Only</span>
                      </div>
                    </Option>
                    <Option value="specific_users">
                      <div className="flex items-center space-x-2">
                        <Target className="w-4 h-4 text-purple-600" />
                        <span>Specific Users</span>
                      </div>
                    </Option>
                    <Option value="policy_compliance_low">
                      <div className="flex items-center space-x-2">
                        <AlertTriangle className="w-4 h-4 text-yellow-600" />
                        <span>Low Policy Compliance Users</span>
                      </div>
                    </Option>
                    <Option value="policy_compliance_critical">
                      <div className="flex items-center space-x-2">
                        <AlertCircle className="w-4 h-4 text-red-600" />
                        <span>Critical Policy Compliance Users</span>
                      </div>
                    </Option>
                    <Option value="no_employment_data">
                      <div className="flex items-center space-x-2">
                        <Info className="w-4 h-4 text-gray-600" />
                        <span>No Employment Data Users</span>
                      </div>
                    </Option>
                  </Select>
                </Form.Item>
              </div>

              {/* Selected Users Display Section - Enhanced */}
              {notificationForm.targetType === 'specific_users' && notificationForm.selectedUsersInfo && notificationForm.selectedUsersInfo.length > 0 && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center shadow-sm">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <span className="text-sm font-semibold text-gray-800">Selected Users</span>
                        <span className="ml-2 px-2.5 py-0.5 bg-blue-500 text-white text-xs font-bold rounded-full">
                          {notificationForm.selectedUsersInfo.length}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setNotificationForm(prev => ({
                          ...prev,
                          targetType: 'all',
                          targetUsers: [],
                          selectedUsersInfo: []
                        }));
                        setSelectedUserIds([]);
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800 font-semibold px-3 py-1 hover:bg-blue-100 rounded-lg transition-colors"
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="space-y-2 max-h-32 overflow-y-auto pr-1">
                    {notificationForm.selectedUsersInfo.map((user) => (
                      <div key={user.id} className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-blue-100 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white text-xs font-bold shadow-sm">
                            {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-800">{user.name || 'No name'}</p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                        </div>
                        <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${
                          user.status === 'active' 
                            ? 'bg-green-100 text-green-700 border border-green-200' 
                            : 'bg-orange-100 text-orange-700 border border-orange-200'
                        }`}>
                          {user.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Push Notification Toggle */}
              <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50">
                <div className="flex items-center gap-2 min-w-0">
                  <Bell className="w-4 h-4 text-blue-600 shrink-0" />
                  <span className="text-sm font-medium text-gray-800">Send Push Notification</span>
                  <span className="hidden sm:inline text-xs text-gray-500 truncate">· Deliver to users' mobile devices</span>
                </div>
                <Form.Item
                  name="sendPushNotification"
                  valuePropName="checked"
                  initialValue={true}
                  className="mb-0"
                >
                  <Switch size="small" checkedChildren="ON" unCheckedChildren="OFF" />
                </Form.Item>
              </div>

              {/* Action Buttons - Enhanced */}
              <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    form.resetFields();
                    setSelectedUserIds([]);
                    setNotificationForm({
                      title: "",
                      message: "",
                      type: "normal",
                      priority: "medium",
                      sender: "admin",
                      targetType: "all",
                      targetUsers: [],
                      scheduledAt: null,
                      isScheduled: false,
                      sendPushNotification: true,
                      requireInteraction: false
                    });
                  }}
                  className="px-4 h-9 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                  style={{ fontSize: '14px' }}
                >
                  Reset Form
                </button>
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => {
                    console.log('🔔 Send notification button clicked');
                    form.validateFields()
                      .then((values) => {
                        console.log('✅ Form validation passed:', values);
                        handleCreateNotification(values);
                      })
                      .catch((errorInfo) => {
                        console.log('❌ Form validation failed:', errorInfo);
                        showToastMessage("Please fill in all required fields.", "error");
                      });
                  }}
                  className="px-5 h-9 text-white bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  style={{ fontSize: '14px' }}
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Bell className="w-4 h-4" />
                      <span>Send Notification</span>
                    </>
                  )}
                </button>
              </div>
            </Form>
          </div>
        </div>

        {/* Compact Notification Log Section */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2.5">
                <input
                  type="checkbox"
                  className="h-4 w-4 cursor-pointer accent-blue-600"
                  checked={notifications.length > 0 && selectedHistoryIds.length === notifications.length}
                  ref={(el) => {
                    if (el) {
                      el.indeterminate =
                        selectedHistoryIds.length > 0 && selectedHistoryIds.length < notifications.length;
                    }
                  }}
                  disabled={notifications.length === 0 || bulkDeleting}
                  onChange={toggleSelectAllHistory}
                  title="전체 선택"
                  aria-label="전체 선택"
                />
                <div>
                  <h3 className="text-sm font-semibold text-gray-800">
                    📋 Notification History
                  </h3>
                  <p className="text-gray-600 text-xs">System notification history and audit trail</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {selectedHistoryIds.length > 0 && (
                  <button
                    type="button"
                    disabled={bulkDeleting}
                    onClick={() => handleBulkDeleteNotifications('selected')}
                    className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 disabled:opacity-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    선택 삭제 ({selectedHistoryIds.length})
                  </button>
                )}
                <button
                  type="button"
                  disabled={notifications.length === 0 || bulkDeleting}
                  onClick={() => handleBulkDeleteNotifications('all')}
                  className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {bulkDeleting ? '삭제 중...' : '모두 삭제'}
                </button>
                <span className="text-sm text-gray-600 font-medium">Total: {notifications.length}</span>
              </div>
            </div>
          </div>

          <div className="p-4 bg-white">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center space-x-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-400/30 border-t-blue-400"></div>
                  <span className="text-gray-600 text-sm font-semibold">Loading...</span>
                </div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Bell className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">No Notifications Found</h3>
                <p className="text-gray-600 mb-4 text-sm">System ready for first notification deployment</p>
                <p className="text-gray-500 text-sm">Use the form above to send your first notification</p>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  {notifications
                    .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                    .map((notification) => (
                      <div key={notification.id} className={`border rounded-lg p-3 transition-all duration-200 ${
                        selectedHistoryIds.includes(notification.id)
                          ? 'bg-blue-50 border-blue-300'
                          : 'bg-gray-50 border-gray-200 hover:border-gray-300 hover:bg-gray-100'
                      }`}>
                        <div className="flex items-center justify-between gap-3">
                          <input
                            type="checkbox"
                            className="h-4 w-4 shrink-0 cursor-pointer accent-blue-600"
                            checked={selectedHistoryIds.includes(notification.id)}
                            disabled={bulkDeleting}
                            onChange={() => toggleHistorySelection(notification.id)}
                            aria-label={`${notification.title || 'notification'} 선택`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-3 mb-1">
                              <h3 className="text-sm font-semibold text-gray-800 truncate">{notification.title}</h3>
                              <span className={`px-2 py-1 text-xs font-medium rounded border flex-shrink-0 ${
                                notification.type === 'urgent' ? 'bg-red-100 text-red-700 border-red-300' :
                                notification.type === 'warning' ? 'bg-orange-100 text-orange-700 border-orange-300' :
                                'bg-blue-100 text-blue-700 border-blue-300'
                              }`}>
                                {notification.type?.toUpperCase() || 'NORMAL'}
                              </span>
                              <span className={`px-2 py-1 text-xs font-medium rounded border flex-shrink-0 ${
                                notification.status === 'sent' ? 'bg-green-100 text-green-700 border-green-300' :
                                notification.status === 'failed' ? 'bg-red-100 text-red-700 border-red-300' :
                                notification.status === 'pending' || !notification.status ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
                                'bg-gray-100 text-gray-700 border-gray-300'
                              }`}>
                                {notification.status === 'sent' ? 'SENT' :
                                 notification.status === 'failed' ? 'FAILED' :
                                 notification.status === 'pending' || !notification.status ? 'PENDING' :
                                 'UNKNOWN'}
                              </span>
                            </div>
                            <p className="text-gray-600 text-xs mb-2 truncate">
                              {(() => {
                                const message = notification.message || notification.body || '';
                                // ID 같은 문자열이 포함된 경우 필터링
                                if (typeof message === 'string' && message.length > 50 && message.includes('_')) {
                                  return 'Notification content is too long or contains IDs.';
                                }
                                return message || 'No notification content.';
                              })()}
                            </p>
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              <div className="flex items-center space-x-1">
                                <Clock className="w-3 h-3" />
                                <span>{formatDate(notification.createdAt)}</span>
                              </div>
                              {notification.sentAt && (
                                <div className="flex items-center space-x-1">
                                  <Send className="w-3 h-3" />
                                  <span>{formatDate(notification.sentAt)}</span>
                                </div>
                              )}
                              {notification.targetCount && (
                                <div className="flex items-center space-x-1">
                                  <Target className="w-3 h-3" />
                                  <span>{notification.targetCount} targets</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            <button
                              onClick={() => handleDeleteNotification(notification.id)}
                              className="p-2 text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors"
                              title="Delete notification"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                console.log('🔍 Button clicked, notification:', notification);
                                setSelectedNotification(notification);
                                setNotificationDetailModal(true);
                              }}
                              className="p-2 text-blue-600 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                              title="View notification details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {(notification.status === 'pending' || notification.status === 'failed' || !notification.status) && (
                              <button
                                onClick={() => handleResendNotification(notification)}
                                className="p-2 text-green-600 bg-green-50 border border-green-200 rounded hover:bg-green-100 transition-colors"
                                title="Resend notification"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
                
                {/* Pagination */}
                <div className="mt-4 bg-gray-50 border-t border-gray-200 px-4 py-4">
                  <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                    {/* Page Info and Page Size Selector */}
                    <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-6">
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Showing:</span> <span className="font-semibold">{((currentPage - 1) * pageSize) + 1}</span> to{' '}
                        <span className="font-semibold">{Math.min(currentPage * pageSize, notifications.length)}</span> of{' '}
                        <span className="font-semibold">{notifications.length}</span> entries
                      </div>
                      
                      {/* Page Size Selector */}
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">Rows:</span>
                        <select
                          value={pageSize}
                          onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setCurrentPage(1); // Reset to first page when changing page size
                          }}
                                className="px-3 py-1 text-sm border border-gray-300 rounded bg-white text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                        </select>
                        <span className="text-sm text-gray-600">per page</span>
                      </div>
                    </div>

                    {/* Pagination Controls */}
                    {Math.ceil(notifications.length / pageSize) > 1 && (
                      <div className="flex items-center space-x-2">
                        {/* First Page Button */}
                        <button
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                          className="p-2 text-gray-600 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors border border-gray-300 hover:border-blue-500 rounded bg-white"
                          title="First page"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          <ChevronLeft className="w-4 h-4 -ml-2" />
                        </button>

                        {/* Previous Page Button */}
                        <button
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className="p-2 text-gray-600 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors border border-gray-300 hover:border-blue-500 rounded bg-white"
                          title="Previous page"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>

                        {/* Page Numbers */}
                        <div className="flex items-center space-x-1">
                          {(() => {
                            const totalPages = Math.ceil(notifications.length / pageSize);
                            const pages = [];
                            
                            // Always show first page
                            if (currentPage > 3) {
                              pages.push(
                                <button
                                  key={1}
                                  onClick={() => setCurrentPage(1)}
                                  className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors font-mono"
                                >
                                  1
                                </button>
                              );
                              if (currentPage > 4) {
                                pages.push(
                                  <span key="ellipsis1" className="px-2 text-gray-500">
                                    ...
                                  </span>
                                );
                              }
                            }

                            // Show pages around current page
                            const startPage = Math.max(1, currentPage - 2);
                            const endPage = Math.min(totalPages, currentPage + 2);
                            
                            for (let i = startPage; i <= endPage; i++) {
                              pages.push(
                                <button
                                  key={i}
                                  onClick={() => setCurrentPage(i)}
                                  className={`px-3 py-1 text-sm rounded border transition-colors font-mono ${
                                    currentPage === i
                                      ? 'bg-cyan-600 text-white border-cyan-500 shadow-lg'
                                      : 'text-gray-300 hover:bg-gray-700 hover:text-white border-gray-600'
                                  }`}
                                >
                                  {i}
                                </button>
                              );
                            }

                            // Always show last page
                            if (currentPage < totalPages - 2) {
                              if (currentPage < totalPages - 3) {
                                pages.push(
                                  <span key="ellipsis2" className="px-2 text-gray-500">
                                    ...
                                  </span>
                                );
                              }
                              pages.push(
                                <button
                                  key={totalPages}
                                  onClick={() => setCurrentPage(totalPages)}
                                  className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors font-mono"
                                >
                                  {totalPages}
                                </button>
                              );
                            }

                            return pages;
                          })()}
                        </div>

                        {/* Next Page Button */}
                        <button
                          onClick={() => setCurrentPage(Math.min(Math.ceil(notifications.length / pageSize), currentPage + 1))}
                          disabled={currentPage === Math.ceil(notifications.length / pageSize)}
                          className="p-2 text-gray-600 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors border border-gray-300 hover:border-blue-500 rounded bg-white"
                          title="Next page"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>

                        {/* Last Page Button */}
                        <button
                          onClick={() => setCurrentPage(Math.ceil(notifications.length / pageSize))}
                          disabled={currentPage === Math.ceil(notifications.length / pageSize)}
                          className="p-2 text-gray-600 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors border border-gray-300 hover:border-blue-500 rounded bg-white"
                          title="Last page"
                        >
                          <ChevronRight className="w-4 h-4" />
                          <ChevronRight className="w-4 h-4 -ml-2" />
                        </button>
                      </div>
                    )}

                    {/* Jump to Page */}
                    {Math.ceil(notifications.length / pageSize) > 5 && (
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-400 font-mono">GOTO:</span>
                        <input
                          type="number"
                          min="1"
                          max={Math.ceil(notifications.length / pageSize)}
                          value={currentPage}
                          onChange={(e) => {
                            const page = parseInt(e.target.value);
                            if (page >= 1 && page <= Math.ceil(notifications.length / pageSize)) {
                              setCurrentPage(page);
                            }
                          }}
                          className="w-16 px-2 py-1 text-sm border border-gray-600 rounded bg-gray-700 text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-center font-mono"
                        />
                        <span className="text-sm text-gray-400 font-mono">
                          of {Math.ceil(notifications.length / pageSize)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

        {/* Create Notification Modal - REMOVED - Moved to top level */}

      {/* Monitoring Modal */}
      <Modal
        title="System Monitoring"
        open={isMonitoringModalVisible}
        onCancel={() => setIsMonitoringModalVisible(false)}
        footer={null}
        width={1000}
      >
        <Tabs>
          <TabPane tab="System Log" key="system">
            <Table
              columns={[
                { title: "Time", dataIndex: "createdAt", key: "createdAt", render: (date) => dayjs(date).format("YYYY-MM-DD HH:mm") },
                { title: "Type", dataIndex: "type", key: "type" },
                { title: "Message", dataIndex: "message", key: "message" },
                { title: "Status", dataIndex: "status", key: "status", render: renderStatus }
              ]}
              dataSource={systemNotifications || []}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              size="small"
            />
          </TabPane>
          <TabPane tab="Activity Log" key="activity">
            <Table
              columns={[
                { 
                  title: "Date", 
                  dataIndex: "createdAt", 
                  key: "createdAt",
                  render: (date) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : 'N/A'
                },
                { 
                  title: "Title", 
                  dataIndex: "title", 
                  key: "title" 
                },
                { 
                  title: "Type", 
                  dataIndex: "type", 
                  key: "type", 
                  render: (type) => {
                    const typeInfo = NOTIFICATION_TYPES[type] || { label: type, color: 'default' };
                    return <Tag color={typeInfo.color} icon={typeInfo.icon}>{typeInfo.label}</Tag>;
                  }
                },
                { 
                  title: "Status", 
                  dataIndex: "status", 
                  key: "status", 
                  render: (status) => {
                    let color = 'default';
                    if (status === 'sent') color = 'success';
                    if (status === 'failed') color = 'error';
                    if (status === 'pending') color = 'processing';
                    return <Tag color={color}>{status}</Tag>;
                  }
                }
              ]}
              dataSource={notifications || []}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              size="small"
            />
          </TabPane>
        </Tabs>
      </Modal>

        {/* Government Grade User Selection Modal - Inside AnimatePresence for proper layering */}
        {isUserSelectionModalVisible && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gradient-to-br from-slate-900/95 via-blue-900/90 to-indigo-900/95 backdrop-blur-xl flex items-center justify-center z-[10003] p-4"
            style={{ 
              position: 'fixed', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0,
              width: '100vw',
              height: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Modal
        title={null}
        open={isUserSelectionModalVisible}
        onCancel={() => {
          setIsUserSelectionModalVisible(false);
          
          // 기존 선택된 사용자가 있다면 유지
          if (notificationForm.selectedUsersInfo && notificationForm.selectedUsersInfo.length > 0) {
            setSelectedUserIds(notificationForm.targetUsers || []);
            console.log('🔄 취소 시 기존 선택 유지:', notificationForm.targetUsers);
          } else {
            // 기존 선택이 없다면 초기화
            setSelectedUserIds([]);
            setUserSearchTerm("");
            setUserSearchResults([]);
            setSearchFilters({
              hasRemittance: "",
              remittanceDays: "",
              userStatus: 'all'
            });
            // 타겟 타입을 all로 리셋
            setNotificationForm(prev => ({
              ...prev,
              targetType: 'all',
              targetUsers: [],
              selectedUsersInfo: []
            }));
          }
        }}
        onOk={confirmUserSelection}
        width={1000}
        okText={`Confirm Selection (${selectedUserIds.length})`}
        cancelText="Cancel"
        okButtonProps={{ 
          disabled: selectedUserIds.length === 0,
          style: { 
            backgroundColor: selectedUserIds.length > 0 ? '#1e40af' : '#d1d5db',
            borderColor: selectedUserIds.length > 0 ? '#1e40af' : '#d1d5db',
            borderRadius: '12px',
            height: '44px',
            fontSize: '14px',
            fontWeight: '600'
          }
        }}
        cancelButtonProps={{
          style: {
            borderRadius: '12px',
            height: '44px',
            fontSize: '14px',
            fontWeight: '600'
          }
        }}
        style={{ zIndex: 10004, position: 'relative' }}
        styles={{
          mask: { zIndex: 10003, backgroundColor: 'transparent' }
        }}
        className="user-selection-modal"
        getContainer={false}
        destroyOnClose={false}
        forceRender={true}
      >
        {/* Elegant Compact Header */}
        <div className="relative bg-gradient-to-r from-slate-700 via-blue-700 to-indigo-700 px-6 py-4 text-white -m-6 mb-4 rounded-t-lg">
          <div className="absolute inset-0 opacity-5" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M10 10c0-2.5-2-4.5-4.5-4.5S1 7.5 1 10s2 4.5 4.5 4.5S10 12.5 10 10zm5 0c0-2.5-2-4.5-4.5-4.5S6 7.5 6 10s2 4.5 4.5 4.5S15 12.5 15 10z'/%3E%3C/g%3E%3C/svg%3E")`
          }} />
          
          <div className="relative flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20 shadow-md">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Select Target Users</h2>
                <p className="text-blue-100 text-xs">Search and select users to receive notifications</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 px-3 py-1 bg-white/10 rounded-lg border border-white/20">
                <div className="w-6 h-6 bg-emerald-400 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">{selectedUserIds.length}</span>
                </div>
                <span className="text-xs font-medium text-emerald-100">Selected</span>
              </div>
              {selectedUserIds.length > 0 && (
                <button
                  onClick={testFCMNotification}
                  className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-lg transition-colors duration-200"
                >
                  Test FCM
                </button>
              )}
            </div>
          </div>
        </div>
        
        <div className="space-y-4">
          {/* Compact Professional Search Section */}
          <div className="bg-gradient-to-br from-slate-50 to-blue-50/50 p-4 rounded-2xl border border-white/50 shadow-md backdrop-blur-sm">
            <div className="space-y-4">
              {/* Compact Search Input */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Search className="w-4 h-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search users..."
                  value={userSearchTerm}
                  className="w-full pl-10 pr-4 py-2.5 bg-white/80 border border-gray-200 rounded-xl text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  onChange={(e) => {
                    setUserSearchTerm(e.target.value);
                    debouncedSearch(e.target.value, searchFilters);
                  }}
                />
                {isSearchingUsers && (
                  <div className="absolute inset-y-0 right-0 pr-6 flex items-center">
                    <div className="w-5 h-5 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
                  </div>
                )}
              </div>

              {/* Compact Advanced Filters */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Remittance Status</label>
                  <Select
                    placeholder="Select remittance status"
                    value={searchFilters.hasRemittance}
                    onChange={(value) => setSearchFilters(prev => ({ ...prev, hasRemittance: value }))}
                    className="w-full"
                    dropdownStyle={{ zIndex: 10004 }}
                  >
                    <Option value="">All Users</Option>
                    <Option value="true">Has Remittance History</Option>
                    <Option value="false">No Remittance History</Option>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">User Status</label>
                  <Select
                    placeholder="Select user status"
                    value={searchFilters.userStatus}
                    onChange={(value) => setSearchFilters(prev => ({ ...prev, userStatus: value }))}
                    className="w-full"
                    dropdownStyle={{ zIndex: 10004 }}
                  >
                    <Option value="all">All Users</Option>
                    <Option value="active">Active Users</Option>
                    <Option value="inactive">Inactive Users</Option>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Remittance Period</label>
                  <Select
                    placeholder="Select remittance period"
                    value={searchFilters.remittanceDays}
                    onChange={(value) => setSearchFilters(prev => ({ ...prev, remittanceDays: value }))}
                    className="w-full"
                    dropdownStyle={{ zIndex: 10004 }}
                  >
                    <Option value="">All Periods</Option>
                    <Option value="7">Last 7 Days</Option>
                    <Option value="30">Last 30 Days</Option>
                    <Option value="90">Last 90 Days</Option>
                  </Select>
                </div>
              </div>
            </div>
          </div>
          
          {/* Compact Professional User List */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/50 shadow-md overflow-hidden max-h-96">
            <div className="p-4 border-b border-gray-200/50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-gray-800">User List</h3>
                  <p className="text-xs text-gray-600 mt-1">
                    {userSearchResults.length > 0 ? `Search results: ${userSearchResults.length}` : displayUsers.length > 0 ? `Displaying: ${displayUsers.length}` : 'Search for users (minimum 2 characters)'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="max-h-96 overflow-y-auto">
              {isSearchingUsers ? (
                <div className="flex items-center justify-center py-20">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
                    <p className="text-gray-600 font-medium">Searching users...</p>
                  </div>
                </div>
              ) : displayUsers.length === 0 ? (
                <div className="flex items-center justify-center py-20">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center">
                      <Users className="w-12 h-12 text-gray-400" />
                    </div>
                    <div className="text-center">
                      <p className="text-gray-600 font-medium">No search results found</p>
                      <p className="text-gray-500 text-sm mt-1">Try a different search term</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-3 space-y-2">
                  {displayUsers.map((user) => (
                    <motion.div
                      key={user.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2 }}
                      className={`p-3 rounded-xl border transition-all duration-200 cursor-pointer transform hover:scale-102 ${
                        selectedUserIds.includes(user.id)
                          ? 'border-blue-500 bg-blue-50/80 shadow-md'
                          : 'border-gray-200 bg-white/80 hover:border-blue-300 hover:shadow-sm'
                      }`}
                      onClick={() => toggleUserSelection(user.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-md">
                            {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-bold text-gray-800">{user.name || 'No name'}</h4>
                            <p className="text-gray-600 text-xs">{user.email}</p>
                            {user.phone && <p className="text-gray-500 text-xs">{user.phone}</p>}
                            <div className="flex items-center space-x-1 mt-1">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                user.hasRemittance 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                {user.hasRemittance ? 'Has Remittance' : 'No Remittance'}
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                user.status === 'active' 
                                  ? 'bg-blue-100 text-blue-700' 
                                  : 'bg-orange-100 text-orange-700'
                              }`}>
                                {user.status === 'active' ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {selectedUserIds.includes(user.id) && (
                            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Compact Selected Users Summary */}
          {selectedUserIds.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-4 text-white shadow-md"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold">Selected Users</h3>
                    <p className="text-emerald-100 text-xs">{selectedUserIds.length} user(s) selected</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 px-3 py-1 bg-white/80 rounded-lg border border-emerald-200">
                  <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">{selectedUserIds.length}</span>
                  </div>
                  <span className="text-xs font-semibold text-emerald-700">Selected</span>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </Modal>
          </motion.div>
        )}

    </div>

    {/* Notification Detail Modal - At the very end */}
    <Modal
      title="📋 Notification Details"
      open={notificationDetailModal}
      onCancel={() => {
        setNotificationDetailModal(false);
        setSelectedNotification(null);
      }}
      footer={[
        <Button key="close" onClick={() => {
          setNotificationDetailModal(false);
          setSelectedNotification(null);
        }}>
          Close
        </Button>
      ]}
      width={700}
      centered
    >
      {selectedNotification && (
        <div style={{ padding: '20px' }}>
          <h3>Basic Information</h3>
          <p><strong>Title:</strong> {selectedNotification.title}</p>
          <p><strong>Type:</strong> {selectedNotification.type}</p>
          <p><strong>Status:</strong> {selectedNotification.status}</p>
          <p><strong>Created:</strong> {selectedNotification.createdAt ? formatDate(selectedNotification.createdAt) : 'N/A'}</p>
          
          <h3>Message Content</h3>
          <div style={{ backgroundColor: '#f5f5f5', padding: '15px', borderRadius: '5px', margin: '10px 0' }}>
            {selectedNotification.message || selectedNotification.body || 'No message content available.'}
          </div>
          
          <h3>Statistics</h3>
          <p><strong>Target Users:</strong> {selectedNotification.targetCount || 0}</p>
          <p><strong>Sent:</strong> {selectedNotification.sentCount || 0}</p>
          <p><strong>Read:</strong> {selectedNotification.read || 0}</p>
          <p><strong>Clicked:</strong> {selectedNotification.clicked || 0}</p>
        </div>
      )}
    </Modal>
    </>
  );
};

export default AdminNotificationPage;
